import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Turning the typed error surface into something a warehouse operator can act on.
 *
 * The distinctions the server draws are not academic, and flattening them here would
 * throw away the work. The two that matter most:
 *
 *   * `InsufficientStockError` — there is not enough. Go and find more, or check whether
 *     the warehouse permits a negative balance.
 *   * `ReservationRefusedError` — there IS enough, and some of it is promised elsewhere.
 *     The remedy is to cite the hold or release it. Sending someone to look for stock that
 *     is sitting in front of them is the failure this separation prevents.
 */
import { Alert, AlertTitle, Box, LinearProgress, Skeleton, Stack, Typography } from '@mui/material';
import { FeatureNotInPlanError, IdempotencyInFlightError, IdempotencyMismatchError, InsufficientStockError, NetworkError, NotFoundError, RequestValidationError, ReservationRefusedError, ResponseShapeError, ServiceUnavailableError, UnauthorizedError, ValidationError, getErrorMessage, } from '@inai/inventory-core';
function explain(error) {
    if (error instanceof InsufficientStockError) {
        return {
            severity: 'warning',
            title: 'Not enough stock',
            body: `${getErrorMessage(error)} Nothing was posted. The movement is refused rather than clamped, so the ledger and the balance still agree.`,
        };
    }
    if (error instanceof ReservationRefusedError) {
        return {
            severity: 'warning',
            title: 'Stock is reserved for something else',
            body: `${getErrorMessage(error)} The stock is on hand but hard-held. Cite the reservation on this issue, or release the hold first — looking for more stock will not help.`,
        };
    }
    if (error instanceof FeatureNotInPlanError) {
        return {
            severity: 'info',
            title: 'Not included in this plan',
            body: `${getErrorMessage(error)} Server-side entitlements decide this; the control was shown because the plan looked current when the screen loaded.`,
        };
    }
    if (error instanceof IdempotencyInFlightError) {
        return {
            severity: 'info',
            title: 'Already being processed',
            body: 'An identical request is still running. It is being retried with the same key, so it cannot post twice.',
        };
    }
    if (error instanceof IdempotencyMismatchError) {
        return {
            severity: 'error',
            title: 'Idempotency key reused',
            body: 'This key was already used for a different request. That is a defect in the caller, not something to retry — reload the screen and start again.',
        };
    }
    if (error instanceof RequestValidationError || error instanceof ValidationError) {
        return { severity: 'warning', title: 'Check these fields', body: getErrorMessage(error) };
    }
    if (error instanceof NotFoundError) {
        return { severity: 'warning', title: 'Not found', body: getErrorMessage(error) };
    }
    if (error instanceof UnauthorizedError) {
        return {
            severity: 'error',
            title: 'Session expired',
            body: 'Sign in again. The module does not manage the session — the host application does.',
        };
    }
    if (error instanceof ServiceUnavailableError) {
        return {
            severity: 'error',
            title: 'Inventory service unreachable',
            body: 'This tenant is in remote mode and the canonical service did not answer. Retrying is safe.',
        };
    }
    if (error instanceof NetworkError) {
        return {
            severity: 'error',
            title: 'Could not reach the server',
            body: 'The request did not get a response. Retrying is safe — it carries the same idempotency key.',
        };
    }
    if (error instanceof ResponseShapeError) {
        return {
            severity: 'error',
            title: 'Unexpected response',
            body: `${error.message} This usually means the module UI and the backend are on different contract versions.`,
        };
    }
    return { severity: 'error', title: 'Something went wrong', body: getErrorMessage(error) };
}
export function ErrorNotice({ error, sx }) {
    if (!error)
        return null;
    const { severity, title, body } = explain(error);
    return (_jsxs(Alert, { severity: severity, sx: { ...sx }, variant: "outlined", children: [_jsx(AlertTitle, { sx: { fontWeight: 700 }, children: title }), _jsx(Typography, { sx: { fontSize: 13, whiteSpace: 'pre-line' }, children: body })] }));
}
export function LoadingBar({ active }) {
    return (_jsx(Box, { sx: { height: 2 }, children: active ? _jsx(LinearProgress, { sx: { height: 2 } }) : null }));
}
export function TableSkeleton({ rows = 6 }) {
    return (_jsx(Stack, { spacing: 1, sx: { p: 2 }, children: Array.from({ length: rows }, (_, i) => (_jsx(Skeleton, { variant: "rounded", height: 34 }, i))) }));
}
export function EmptyState({ title, hint }) {
    return (_jsxs(Box, { sx: { p: 6, textAlign: 'center' }, children: [_jsx(Typography, { sx: { fontWeight: 600 }, color: "text.secondary", children: title }), hint ? (_jsx(Typography, { sx: { fontSize: 13, mt: 0.75 }, color: "text.disabled", children: hint })) : null] }));
}
//# sourceMappingURL=Feedback.js.map